import React from "react";
import ButtonVector from "../../../assets/buttonVector.svg";
import carasolImage1 from "../../../assets/homePageCarasolImage1.svg";
import carasolImage2 from "../../../assets/homePageCarasolImage2.svg";
import carasolImage3 from "../../../assets/homePageCarasolImage3.svg";
import carasolImage4 from "../../../assets/homePageCarasolImage4.svg";
import Slider from "react-slick";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Training = ({ data }) => {
  const location = useLocation();
  const pathParts = location.pathname.split("/");
  const activePath = pathParts[pathParts.length - 1];

  const carasolSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 800,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    pauseOnHover: true,
    cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 400,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  // Check if we have dynamic training data
  const hasTrainingData =
    data?.content?.Training?.Images && data.content.Training.Images.length > 0;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex flex-col py-16 lg:py-24 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>
      </div>

      {/* Header Section */}
      <motion.div
        variants={itemVariants}
        className="text-center mb-12 lg:mb-16 relative z-10"
      >
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-6 lg:gap-8">
          <div className="flex flex-col w-full text-center justify-center items-center">
            <motion.h1
              className="font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight text-[#14183E] mb-4"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-[#183864] bg-clip-text">
                {data?.content?.Training?.Heading || "Our Training Partners"}
              </span>
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-[#5E6282] font-medium leading-relaxed max-w-3xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {data?.content?.Training?.SubHeading ||
                "Trusted by industry leaders and recognized institutions worldwide"}
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Training Partners Slider */}
      <motion.div variants={itemVariants} className="relative z-10">
        <div className="training-slider-container relative">
          <div className="relative">
            {hasTrainingData && (
              <Slider {...carasolSettings} className="fancy-slider">
                {data.content.Training.Images.map((image, index) => (
                  <div key={index} className="px-3">
                    <motion.div
                      className="relative"
                      whileHover={{ scale: 1.05, y: -5 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}/${image}`}
                        className="h-16 lg:h-40 w-40 object-contain max-w-full"
                        alt={`Training Partner ${index + 1}`}
                        onError={(e) => {
                          console.error("Image load error:", e.target.src);
                          e.target.style.display = "none";
                        }}
                      />
                    </motion.div>
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </div>
      </motion.div>

      {/* Enhanced CSS Styles */}
      <style jsx>{`
        .training-slider-container {
          position: relative;
        }

        .fancy-slider .slick-dots {
          bottom: -50px;
          display: flex !important;
          justify-content: center;
          align-items: center;
          gap: 8px;
          padding: 0;
          margin: 0;
          list-style: none;
        }

        .fancy-slider .slick-dots li {
          width: 10px;
          height: 10px;
          margin: 0 4px;
        }

        .fancy-slider .slick-dots li button {
          width: 10px;
          height: 10px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: #cbd5e1;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0;
          line-height: 0;
          text-indent: -9999px;
        }

        .fancy-slider .slick-dots li button:hover {
          background: #64748b;
          transform: scale(1.1);
        }

        .fancy-slider .slick-dots li.slick-active button {
          background: #b02526;
          transform: scale(1.2);
          box-shadow: 0 2px 8px rgba(176, 37, 38, 0.3);
        }

        .fancy-slider .slick-track {
          display: flex;
          align-items: center;
          margin: 0;
        }

        .fancy-slider .slick-slide {
          height: auto;
          opacity: 0.7;
          transition: opacity 0.5s ease;
        }

        .fancy-slider .slick-slide.slick-active {
          opacity: 1;
        }

        .fancy-slider .slick-slide > div {
          height: 100%;
          display: flex;
          align-items: center;
        }

        .fancy-slider .slick-list {
          overflow: hidden;
          margin: 0 -12px;
          padding: 20px 0;
        }

        @media (max-width: 768px) {
          .fancy-slider .slick-dots {
            bottom: -40px;
          }

          .fancy-slider .slick-dots li button {
            width: 10px;
            height: 10px;
          }
        }

        /* Responsive dots */
        @media (max-width: 768px) {
          .fancy-slider .slick-dots {
            bottom: -40px;
            gap: 6px;
          }

          .fancy-slider .slick-dots li {
            width: 8px;
            height: 8px;
            margin: 0 3px;
          }

          .fancy-slider .slick-dots li button {
            width: 8px;
            height: 8px;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Training;
